import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Tag } from 'lucide-react';
import api from '@/lib/api';
import { formatDate, formatCurrency } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';

interface Coupon {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  usage_count: number;
  /** Server column name (migration 003); the list used to read a `max_uses` that never existed. */
  usage_limit: number | null;
  starts_at: string | null;
  expires_at: string | null;
  is_active: boolean;
}

interface CouponsResponse {
  coupons: Coupon[];
}

export default function Coupons() {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; couponId: string | null }>({
    isOpen: false,
    couponId: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  async function fetchCoupons() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<CouponsResponse>('/admin/coupons');
      setCoupons(response.coupons);
    } catch (err) {
      setError('Failed to load coupons. Please try again.');
      console.error('Error fetching coupons:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDelete(couponId: string) {
    try {
      setIsDeleting(true);
      await api.delete(`/admin/coupons/${couponId}`);
      setCoupons(coupons.filter(c => c.id !== couponId));
      setDeleteModal({ isOpen: false, couponId: null });
    } catch (err) {
      setError('Failed to delete coupon. Please try again.');
      console.error('Error deleting coupon:', err);
    } finally {
      setIsDeleting(false);
    }
  }

  function openDeleteModal(couponId: string) {
    setDeleteModal({ isOpen: true, couponId });
  }

  function closeDeleteModal() {
    setDeleteModal({ isOpen: false, couponId: null });
  }

  function getDiscountBadgeVariant(type: string) {
    return type === 'percentage' ? 'blue' : 'green';
  }

  function formatDiscountValue(coupon: Coupon) {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}%`;
    }
    return formatCurrency(coupon.discount_value);
  }

  function formatUsageLimit(coupon: Coupon) {
    if (coupon.usage_limit == null) {
      return `${coupon.usage_count} / Unlimited`;
    }
    return `${coupon.usage_count} / ${coupon.usage_limit}`;
  }

  function formatValidDates(coupon: Coupon) {
    const start = coupon.starts_at ? formatDate(new Date(coupon.starts_at)) : 'No start date';
    const end = coupon.expires_at ? formatDate(new Date(coupon.expires_at)) : 'No expiration';
    return `${start} - ${end}`;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading coupons...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Tag className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Coupons</h1>
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={() => navigate('/admin/coupons/new')}
          aria-label="Add new coupon"
        >
          <Plus className="w-5 h-5 mr-2" aria-hidden="true" />
          Add Coupon
        </Button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full" role="table" aria-label="Coupons list">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid Dates
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {coupons.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <Tag className="w-12 h-12 mx-auto mb-3 text-gray-400" aria-hidden="true" />
                    <p className="text-lg">No coupons yet</p>
                    <p className="text-sm mt-1">Create your first coupon to get started</p>
                  </td>
                </tr>
              ) : (
                coupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/coupons/${coupon.id}`}
                        className="font-mono font-semibold text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                      >
                        {coupon.code}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={getDiscountBadgeVariant(coupon.discount_type)}>
                        {coupon.discount_type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-gray-900 font-medium">
                      {formatDiscountValue(coupon)}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {formatUsageLimit(coupon)}
                    </td>
                    <td className="px-6 py-4 text-gray-600 text-sm">
                      {formatValidDates(coupon)}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={coupon.is_active ? 'green' : 'gray'}>
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/coupons/${coupon.id}`)}
                        aria-label={`Edit coupon ${coupon.code}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => openDeleteModal(coupon.id)}
                        aria-label={`Delete coupon ${coupon.code}`}
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        title="Delete Coupon"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete this coupon? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={closeDeleteModal}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteModal.couponId && handleDelete(deleteModal.couponId)}
              isLoading={isDeleting}
            >
              Delete Coupon
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
